import { useEffect, useRef } from "react";
import { Col, Row } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useAddSolarPlantChangelogEntryMutation } from "../../../../../redux/api/generation/generationAPI";
import { IChangelogEntryView } from "../../../../../shared/oversight-core/interfaces/change-log-entry-view";
import { IHttpError } from "../../../../../shared/oversight-core/interfaces/http-errror";
import AppInput from "../../../../../shared/oversight-core/ui-elements/input/app-input";
import ModalContainer, {
  ModelContainerProps,
} from "../../../../../shared/oversight-core/ui-elements/modal-container/modal-container";
import {
  showErrorMessage,
  showSuccessMessage,
} from "../../../../../shared/oversight-core/utils/toast";
import { useCanOperate } from "../../../../../shared/utils/org-roles";

interface IProps extends ModelContainerProps {
  show: boolean;
  plantId: string;
  changelogs: IChangelogEntryView[];
  onSuccess?: () => void;
}

interface FormValues {
  note: string;
}

const defaultFormValues: FormValues = {
  note: "",
};

const ChangeLogModal = (props: IProps) => {
  const { show, plantId, changelogs, onSuccess, onClose, onCancel, ...rest } =
    props;
  // A viewer reads the change log; adding to it is a change to the site.
  const canOperate = useCanOperate();

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    reset,
  } = useForm<FormValues>({
    defaultValues: { ...defaultFormValues },
    mode: "onChange",
  });

  const [triggerAddChangelog, { isLoading }] =
    useAddSolarPlantChangelogEntryMutation();

  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!show) {
      reset({ ...defaultFormValues });
    } else {
      setTimeout(() => {
        if (scrollRef.current) {
          scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
      }, 100);
    }
  }, [show, reset]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [changelogs]);

  const onSubmit = (data: FormValues) => {
    triggerAddChangelog({
      plantId,
      note: data.note.trim(),
    })
      .unwrap()
      .then(() => {
        showSuccessMessage("Change log added successfully");
        reset({ ...defaultFormValues });
        onSuccess?.();
      })
      .catch((error: IHttpError) => {
        showErrorMessage(error.message || "Failed to add change log");
      });
  };

  const handleClose = () => {
    onClose?.();
    reset({ ...defaultFormValues });
  };

  const handleCancel = () => {
    onCancel?.();
    reset({ ...defaultFormValues });
  };

  return (
    <ModalContainer
      {...rest}
      show={show}
      onClose={handleClose}
      onCancel={handleCancel}
      onConfirm={handleSubmit(onSubmit)}
      size="modal-md"
      confirmButtonVariant="blue"
      hideConfirm={!canOperate}
      cancelButtonText={canOperate ? undefined : "Close"}
      title="Change Log"
      isLoading={isLoading}
      disabled={!isValid}
    >
      <div className="d-flex flex-column h-100">
        <div
          ref={scrollRef}
          className="flex-grow-1 overflow-auto mb-2"
          style={{ maxHeight: "300px", minHeight: "150px" }}
        >
          {changelogs && changelogs.length > 0 ? (
            changelogs.map((log, index) => (
              <div key={index} className="mb-2 p-2 rounded bg-background">
                <div className="text-light font-weight-400 font-size-14 mb-1">
                  {log.note}
                </div>
                <div className="d-flex justify-content-between text-light font-size-12">
                  <span>{log.addedBy}</span>
                  <span>
                    {log.date ? new Date(log.date).toLocaleString() : ""}
                  </span>
                </div>
              </div>
            ))
          ) : (
            <div className="text-muted text-center py-4 font-size-14">
              No change logs found.
            </div>
          )}
        </div>
        {canOperate && (
          <Row>
            <Col>
              <AppInput
                label="Note"
                placeholder="Enter note..."
                name="note"
                register={register("note", {
                  required: "Note is required",
                })}
                errors={errors}
              />
            </Col>
          </Row>
        )}
      </div>
    </ModalContainer>
  );
};

export default ChangeLogModal;
